import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import {
  Package,
  FolderTree,
  Mail,
  MessageSquare,
  Smartphone,
  Pencil,
  Trash2,
  Plus,
  RefreshCw,
} from "lucide-react";
import { AdminDataTable, type ColumnDef, StatusBadge, EmptyState } from "../shared";
import { AlertRuleForm } from "./AlertRuleForm";
import {
  useStockAlertRules,
  useDeleteAlertRule,
} from "@/hooks/admin/useStockAlerts";
import type { StockAlertRule } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Channel icon helper
// ---------------------------------------------------------------------------

function ChannelIcons({ rule }: { readonly rule: StockAlertRule }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {rule.notify_email && (
        <Mail className="h-4 w-4 text-stone-500" aria-label="Email" />
      )}
      {rule.notify_sms && (
        <Smartphone className="h-4 w-4 text-stone-500" aria-label="SMS" />
      )}
      {rule.notify_whatsapp && (
        <MessageSquare className="h-4 w-4 text-emerald-600" aria-label="WhatsApp" />
      )}
      {!rule.notify_email && !rule.notify_sms && !rule.notify_whatsapp && (
        <span className="text-xs text-stone-400">--</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AlertRulesTable() {
  const { t } = useTranslation("admin");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<StockAlertRule | null>(null);

  const { data, isLoading, error } = useStockAlertRules(page);
  const deleteRule = useDeleteAlertRule();

  const handleEdit = useCallback((rule: StockAlertRule) => {
    setEditingRule(rule);
    setFormOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingRule(null);
    setFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormOpen(false);
    setEditingRule(null);
  }, []);

  const handleDelete = useCallback(
    (rule: StockAlertRule) => {
      if (!window.confirm(t("stock_alerts.confirm_delete"))) return;
      deleteRule.mutate(rule.id);
    },
    [deleteRule, t],
  );

  // Column definitions
  const columns: readonly ColumnDef<StockAlertRule>[] = [
    {
      id: "scope",
      header: t("stock_alerts.col_scope"),
      sortable: false,
      cell: (rule) => (
        <span className="inline-flex items-center gap-2">
          {rule.scope === "product" ? (
            <Package className="h-4 w-4 text-amber-600" />
          ) : (
            <FolderTree className="h-4 w-4 text-indigo-600" />
          )}
          <span className="text-sm font-medium text-stone-800">
            {rule.scope === "product"
              ? rule.product?.name ?? "--"
              : rule.category?.name ?? "--"}
          </span>
        </span>
      ),
    },
    {
      id: "threshold",
      header: t("stock_alerts.col_threshold"),
      sortable: true,
      cell: (rule) => (
        <span className="tabular-nums font-medium text-stone-700">
          {rule.threshold}
        </span>
      ),
    },
    {
      id: "channels",
      header: t("stock_alerts.col_channels"),
      sortable: false,
      cell: (rule) => <ChannelIcons rule={rule} />,
    },
    {
      id: "auto_reorder",
      header: t("stock_alerts.col_auto_reorder"),
      sortable: false,
      mobileVisible: false,
      cell: (rule) =>
        rule.auto_reorder ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            <RefreshCw className="h-3 w-3" />
            {t("stock_alerts.auto_reorder_on")}
          </span>
        ) : (
          <span className="text-xs text-stone-400">--</span>
        ),
    },
    {
      id: "status",
      header: t("stock_alerts.col_status"),
      sortable: false,
      cell: (rule) => (
        <StatusBadge
          status={rule.is_active ? "active" : "inactive"}
          variant="order"
        />
      ),
    },
    {
      id: "actions",
      header: t("stock_alerts.col_actions"),
      sortable: false,
      mobileVisible: false,
      cell: (rule) => (
        <span className="inline-flex items-center gap-1">
          <button
            onClick={() => handleEdit(rule)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label={t("actions.edit")}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleDelete(rule)}
            className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
            aria-label={t("actions.delete")}
            disabled={deleteRule.isPending}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </span>
      ),
    },
  ];

  // Error state
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">{t("stock_alerts.error_loading")}</p>
      </div>
    );
  }

  const rules = data?.rules ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-stone-900">
          {t("stock_alerts.title")}
        </h3>
        <Button onClick={handleCreate} size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t("stock_alerts.add_rule")}
        </Button>
      </div>

      {/* Table */}
      {!isLoading && rules.length === 0 ? (
        <EmptyState
          title={t("stock_alerts.empty_title")}
          description={t("stock_alerts.empty_description")}
        />
      ) : (
        <AdminDataTable<StockAlertRule>
          columns={columns}
          data={rules}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setPage}
          emptyMessage={t("stock_alerts.empty_title")}
        />
      )}

      {/* Create / Edit modal */}
      <AlertRuleForm
        open={formOpen}
        onClose={handleCloseForm}
        rule={editingRule}
      />
    </div>
  );
}
